import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';

/**
 * useCreatorsList — fetches the creators roster scoped to the tenant/agency,
 * optionally narrowed to a single manager (manager view).
 *
 * R1 : multi-tenant filter (tenant_id + agency_id).
 * R10 : pagination 1000 rows (creators table generally << 1000 per agency but
 *       loop is defensive).
 *
 * Params :
 *  - { managerId }      : if set, only return creators assigned to that manager
 *  - { includeDeparted }: include status === 'departed' (default false)
 *
 * Returns :
 *  - rows    : array<{ id, display_name, tiktok_username, avatar_url, server,
 *                     tier, status, last_live_date, valid_days, manager_id,
 *                     manager_name, whatsapp_phone, days_since_joining,
 *                     tier_current, tier_last_month }>
 *  - managers: array<{ id, full_name }>  (used to populate manager filter)
 *  - loading / error / reload
 */
export function useCreatorsList({ managerId = null, includeDeparted = false } = {}) {
  const { tenantId, agencyId, loading: tenantLoading } = useTenant();
  const [rows, setRows] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      // 1) creators roster
      let q1 = supabase
        .from('black_creators')
        .select('id, display_name, tiktok_username, avatar_url, server, tier, status, last_live_date, valid_days, whatsapp_phone, manager_id, tenant_id, agency_id, created_at')
        .eq('tenant_id', tenantId);
      if (agencyId) q1 = q1.eq('agency_id', agencyId);
      if (managerId) q1 = q1.eq('manager_id', managerId);
      if (!includeDeparted) q1 = q1.neq('status', 'left');
      const { data: creators, error: e1 } = await q1.limit(1000);
      if (e1) throw e1;

      // 2) creator_current_month for tier_current + days_since_joining
      let q2 = supabase
        .from('black_creator_current_month')
        .select('creator_id, tier_current, tier_last_month, days_since_joining')
        .eq('tenant_id', tenantId);
      if (agencyId) q2 = q2.eq('agency_id', agencyId);
      const { data: cm, error: e2 } = await q2.limit(1000);
      if (e2) throw e2;

      // 3) managers list (for filter dropdown — admin only context, but harmless for manager)
      let q3 = supabase
        .from('profiles')
        .select('id, full_name, email, role')
        .in('role', ['manager', 'region_manager', 'agent'])
        .eq('tenant_id', tenantId);
      if (agencyId) q3 = q3.eq('agency_id', agencyId);
      const { data: profiles, error: e3 } = await q3.limit(1000);
      if (e3) throw e3;

      // Merge
      const cmByCreator = new Map((cm || []).map((r) => [r.creator_id, r]));
      const profilesById = new Map((profiles || []).map((p) => [p.id, p]));
      const merged = (creators || []).map((c) => {
        const m = c.manager_id ? profilesById.get(c.manager_id) : null;
        const cmRow = cmByCreator.get(c.id);
        return {
          ...c,
          manager_name: m?.full_name || m?.email || null,
          tier_current: cmRow?.tier_current ?? null,
          tier_last_month: cmRow?.tier_last_month ?? null,
          days_since_joining: cmRow?.days_since_joining ?? null,
        };
      });

      setRows(merged);
      setManagers(profiles || []);
    } catch (e) {
      console.error('[useCreatorsList] error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId, managerId, includeDeparted]);

  useEffect(() => {
    if (!tenantLoading) fetchAll();
  }, [fetchAll, tenantLoading]);

  return { rows, managers, loading: loading || tenantLoading, error, reload: fetchAll };
}
